import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';

export interface InfrastructureOption {
  entity: Entity;
  name: string;
  type: string;
  account: string;
  lifecycle: string;
  region: string;
  system: string;
  value: string; // The entity reference string to be stored in the form
}

export const useInfrastructureData = (selectedRepositorySystem?: string) => {
  const catalogApi = useApi(catalogApiRef);
  const [infrastructures, setInfrastructures] = useState<
    InfrastructureOption[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInfrastructures = async () => {
      if (!selectedRepositorySystem) {
        setInfrastructures([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'Infrastructure',
            'spec.system': `system:default/${selectedRepositorySystem}`,
          },
        });

        const infrastructureOptions: InfrastructureOption[] = items
          .filter(entity => entity.spec)
          .map(entity => {
            const spec = entity.spec as any;
            const name = entity.metadata.name;
            const type = spec.type || 'Unknown';
            const account = spec.account || 'Unknown';
            const lifecycle = spec.lifecycle || 'Unknown';
            const region = spec.region || 'Unknown';
            const system = spec.system || 'Unknown';
            const value = stringifyEntityRef(entity);

            return {
              entity,
              name,
              type,
              account,
              lifecycle,
              region,
              system,
              value,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setInfrastructures(infrastructureOptions);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to fetch infrastructures',
        );
        setInfrastructures([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInfrastructures();
  }, [catalogApi, selectedRepositorySystem]);

  return { infrastructures, loading, error };
};
